import InvoiceList from './InvoiceList'
import InvoiceDiscrepancyNotice from './InvoiceDiscrepancyNotice';
import { hasOnlyDepositInvoices } from './invoiceDiscrepancy';
import { useState, useEffect, useMemo } from 'react'
import { fetchInvoicesListByOrder, resendInvoiceEmail, createInvoice, fetchOrderBalanceStatus, resolveInvoiceCreationPreparation, fetchInvoiceAddressPrecheck, notifyInvoiceAddressMissing, getPublicInvoice, previewResetInvoiced, confirmResetInvoiced } from "../../services/invoices";
import {
    Alert,
    Avatar,
    Chip,
    Tooltip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    InputAdornment,
    CircularProgress,
    DialogActions,
    Typography,
    Box,
    TextField,
    FormControlLabel,
    Checkbox,
    Switch
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { normalizeAvatarUrl } from '../../utils/avatar';
import InvoiceEdit from './InvoiceEdit';
import { useParams, useHistory } from 'react-router-dom';
import EmailComposer from '../EmailComposer';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  getInvoicePermissionState,
  normalizeInvoiceRoles,
  resolveInvoiceRouteAccess,
} from '../../utils/invoicePermissions';
import DepositCheckPanel from '../DepositCheck/DepositCheckPanel';
import {
  CREATE_INVOICE_MODE_OPTIONS,
  DEFAULT_CREATE_INVOICE_MODE,
  DEFAULT_ENABLED_CREATE_INVOICE_MODES,
  getCreateInvoiceModeLabel,
  normalizeCreateInvoiceModeConfig,
} from '../../constants/createInvoiceModes';
import { filterActiveInvoices } from '../../utils/invoiceVisibility';
import { missingInvoiceAddresses } from '../../utils/invoiceAddressCompleteness';
import InvoiceCreationReview from './InvoiceCreationReview';
import {
  resolveRemainingInvoiceableAmount,
  sumActiveInvoiceTotal,
} from '../../utils/invoiceAvailability';

const toNumber = (value) => {
  if (value && typeof value === 'object') {
    if (value.amount != null) return toNumber(value.amount);
    if (value.total != null) return toNumber(value.total);
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const parseInvoiceRelationships = (raw) => {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  }
  return {};
};

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatMoney = (amount, currency = 'CAD') => {
  const n = Number(amount || 0);
  return `${String(currency || 'CAD').toUpperCase()} ${n.toFixed(2)}`;
};

const buildGeneratedInvoiceUrl = (invoiceId) => {
  const base = process.env.REACT_APP_FRONTEND_URL || window.location.origin;
  return invoiceId ? `${base}/generated-invoice/${invoiceId}` : '';
};

const pickInvoiceNumber = (invoice = {}) =>
  invoice?.qboDocNumber ||
  invoice?.docNumber ||
  invoice?.invoiceNumber ||
  invoice?.id ||
  '';

const pickInvoiceAmount = (invoice = {}, fallbackAmount = 0) =>
  invoice?.amount ||
  invoice?.total ||
  invoice?.grandTotal ||
  invoice?.amountDue ||
  fallbackAmount ||
  0;

const pickPaymentLink = (invoice = {}) =>
  invoice?.checkoutLink ||
  invoice?.checkoutUrl ||
  invoice?.paymentLink ||
  '';

const buildInvoiceEmailSubject = ({ invoice, orderId, order, projectName }) => {
  const invoiceNumber = pickInvoiceNumber(invoice);
  const resolvedProjectName =
    projectName ||
    order?.ProjectName ||
    order?.projectName ||
    invoice?.projectName ||
    `Project ${invoice?.orderId || orderId}`;

  return `Invoice ${invoiceNumber ? `#${invoiceNumber}` : ''} for ${resolvedProjectName}`.replace(/\s+/g, ' ').trim();
};

const buildInvoiceEmailBody = ({ invoice, orderId, order, projectName: projectNameOverride, amountOverride }) => {
  const invoiceId = invoice?.id;
  const invoiceNumber = pickInvoiceNumber(invoice);
  const projectName =
    projectNameOverride ||
    order?.ProjectName ||
    order?.projectName ||
    invoice?.projectName ||
    `Project ${invoice?.orderId || orderId}`;

  const customerName =
    invoice?.billingName ||
    invoice?.customerName ||
    order?.CustomerName ||
    '';

  const amount = pickInvoiceAmount(invoice, amountOverride);
  const currency = invoice?.currency || order?.currency || 'CAD';
  const status = invoice?.paymentStatus || invoice?.invoiceStatus || 'Open';
  const isPaidCopy = [invoice?.paymentStatus, invoice?.invoiceStatus]
    .some((value) => ['paid', 'completed', 'succeeded', 'success'].includes(String(value || '').toLowerCase()));
  const paymentLink = pickPaymentLink(invoice);
  const invoiceUrl = buildGeneratedInvoiceUrl(invoiceId);

  return `
    <p>Hi${customerName ? ` ${escapeHtml(customerName)}` : ''},</p>
    <p>${isPaidCopy ? 'Here is a copy of your paid invoice for' : 'Your invoice for'} <strong>${escapeHtml(projectName)}</strong>${isPaidCopy ? '.' : ' is ready.'}</p>
    <table cellpadding="6" cellspacing="0" style="border-collapse:collapse;margin:12px 0;">
      <tr><td><strong>Invoice #</strong></td><td>${escapeHtml(invoiceNumber || invoiceId || '—')}</td></tr>
      <tr><td><strong>Project</strong></td><td>${escapeHtml(projectName)}</td></tr>
      <tr><td><strong>${isPaidCopy ? 'Invoice Total' : 'Amount Due'}</strong></td><td>${escapeHtml(formatMoney(amount, currency))}</td></tr>
      ${isPaidCopy ? `<tr><td><strong>Balance Due</strong></td><td>${escapeHtml(formatMoney(0, currency))}</td></tr>` : ''}
      <tr><td><strong>Status</strong></td><td>${escapeHtml(status)}</td></tr>
    </table>
    ${!isPaidCopy && paymentLink ? `<p><a href="${escapeHtml(paymentLink)}">Pay invoice online</a></p>` : ''}
    ${invoiceUrl ? `<p><a href="${escapeHtml(invoiceUrl)}">View invoice</a></p>` : ''}
    <p>Thank you,</p>
  `;
};

const getPreferredInvoiceLayout = (currentLayout) => {
  if (currentLayout && currentLayout !== 'plain') return currentLayout;
  return currentLayout || 'plain';
};

const getCreateModeLabel = (mode) => {
  return getCreateInvoiceModeLabel(mode);
};

const getCreateModeSuccessLabel = (mode) => {
  if (mode === 'manual') return 'Manual invoice draft created.';
  if (mode === 'automated') return 'Stripe invoice draft created.';
  return 'Customer selection invoice draft created.';
};

const resolveDefaultCreateInvoiceAmount = ({
  balanceStatus,
  remainingInvoiceable,
}) => {
  const uninvoicedAdditionalBalance = toNumber(balanceStatus?.uninvoicedAdditionalBalance);
  if (Number.isFinite(uninvoicedAdditionalBalance) && uninvoicedAdditionalBalance > 0) {
    return uninvoicedAdditionalBalance.toFixed(2);
  }

  const normalizedRemainingInvoiceable = Number(remainingInvoiceable || 0);
  if (Number.isFinite(normalizedRemainingInvoiceable) && normalizedRemainingInvoiceable > 0) {
    return normalizedRemainingInvoiceable.toFixed(2);
  }

  return '';
};

export const getInvoiceAvailabilityNotice = (balanceStatus) => {
  if (!balanceStatus) return '';
  if (balanceStatus.hasUninvoicedAdditionalBalance) {
    const covered = toNumber(balanceStatus.activeInvoiceTotal);
    // When nothing has been invoiced, the empty invoice list and progress
    // header already explain the situation. Do not display a redundant
    // financial-looking alert for an ordinary first invoice.
    if (covered <= 0.01) return '';
    const amount = toNumber(balanceStatus.uninvoicedAdditionalBalance).toFixed(2);
    return `Uninvoiced balance: ${amount}. Active invoices already cover part of this Sales Order; you can generate a balance invoice for the remainder.`;
  }
  return balanceStatus.hasAdditionalBalanceDue
    ? 'An outstanding customer balance is already covered by an active invoice.'
    : '';
};

export const getSalesOrderInvoiceTotalMismatch = ({
  balanceStatus,
  liveSalesOrderCartTotal = 0,
} = {}) => {
  const savedTotal = toNumber(balanceStatus?.currentOrderTotal);
  const liveCartTotal = toNumber(liveSalesOrderCartTotal);
  const activeSnapshotTotal = toNumber(balanceStatus?.activeSalesOrderSnapshotCalculatedTotal);
  const discrepancy = [
    { label: 'current Sales Order cart', total: liveCartTotal },
    { label: 'active Sales Order snapshot', total: activeSnapshotTotal },
  ].find(({ total }) => savedTotal > 0 && total > 0 &&
    Math.abs(Math.round(savedTotal * 100) - Math.round(total * 100)) > 1);
  if (!discrepancy) return '';
  const { label: sourceLabel, total: comparableTotal } = discrepancy;
  return `Financial totals need review: the saved Sales Order amount is ${savedTotal.toFixed(2)}, but the ${sourceLabel} calculates ${comparableTotal.toFixed(2)}. Review the approved document before creating an invoice. The saved amount has not been changed automatically.`;
};

export const InvoiceBalanceHelperText = ({
  balanceLoading,
  balanceError,
  balanceStatus,
  defaultCreateInvoiceAmount,
  remainingInvoiceable,
}) => {
  if (balanceLoading) return 'Calculating remaining invoiceable amount…';
  if (balanceError) return 'Unable to calculate the remaining invoiceable amount. You may enter an amount manually.';
  if (balanceStatus?.hasUninvoicedAdditionalBalance) {
    return `There is currently ${Number(balanceStatus.uninvoicedAdditionalBalance || 0).toFixed(2)} left to be invoiced without duplicating an active invoice.`;
  }
  return `There is currently ${defaultCreateInvoiceAmount || remainingInvoiceable.toFixed(2)} left to be invoiced. You may choose a smaller amount for a partial invoice.`;
};

// Pure gate for the one-time Create Invoice opening spinner. A rejected request
// still counts as settled; its existing error message appears in the form.
export const areCreateInvoiceChecksSettled = ({
  financialConfigReady = false,
  qboDefaultsReady = false,
  invoiceListReady = false,
  balanceLoading = true,
  initialAmountSynced = false,
  initialAddressCheckSettled = false,
  addressPrecheckLoading = false,
  enableInvoiceCreationReview = false,
  initialReviewSettled = false,
} = {}) => Boolean(
  financialConfigReady && qboDefaultsReady && invoiceListReady &&
  !balanceLoading && initialAmountSynced &&
  initialAddressCheckSettled && !addressPrecheckLoading &&
  (!enableInvoiceCreationReview || initialReviewSettled)
);

const isInvoicePaid = (invoice) => {
  const payment = String(invoice?.paymentStatus || '').trim().toLowerCase();
  const status = String(invoice?.invoiceStatus || '').trim().toLowerCase();
  return (
    ['paid', 'completed', 'succeeded', 'success'].includes(payment) ||
    ['paid', 'completed'].includes(status)
  );
};

const displayInvoiceWorkflowStatus = (value) => {
  const status = String(value ?? '').trim();
  if (status === '9' || status.toLowerCase() === 'invoiced') return 'Invoiced';
  if (status === '8' || status.toLowerCase() === 'ready to be invoiced') return 'Ready to be Invoiced';
  return status || 'Unknown';
};

const Invoices = ({
    clientContacts,
    authState,
    salesOrderTotal = 0,
    order = null,
    invoicingCartKey = null,
    activeInvoicingCartSummary = null,
    invoiceCartSelector = null,
    onCreateInvoicePathA = null,
    canViewInvoices: canViewInvoicesProp,
    canManageInvoices: canManageInvoicesProp,
    readOnlyInvoiceAccess: readOnlyInvoiceAccessProp,
    invoiceCount: invoiceCountProp = 0,
    onInvoicesChanged = null,
    onInitialInvoiceDataReady = null,
    sourceSalesOrderId = null,
}) => {
    const [invoices, setInvoices] = useState([])
    const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
    const { instanceUniqueId, orderId, tabId, tabtype } = useParams();
    const history = useHistory();
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [resendOpen, setResendOpen] = useState(false);
    const [resendTarget, setResendTarget] = useState(null);
    const [emailAttachments, setEmailAttachments] = useState([]);
    const [resendSending, setResendSending] = useState(false);
    const [financeFromOptions, setFinanceFromOptions] = useState([]);
    const [resendSenderPolicy, setResendSenderPolicy] = useState({ mode: 'user_selectable', from: null });
    const [resendSenderPolicyLoading, setResendSenderPolicyLoading] = useState(false);
    const [resendMissingAddresses, setResendMissingAddresses] = useState([]);
    const [confirmSendWithMissingAddresses, setConfirmSendWithMissingAddresses] = useState(false);
    const resendSenderLocked = resendSenderPolicy.mode !== 'user_selectable';
    const [resendEmailValue, setResendEmailValue] = useState({
      to: [],
      cc: [],
      bcc: [],
      from: '',
      subject: '',
      body: '',
      templateId: '',
      layout: 'plain',
      quickActions: {
        bccMe: false,
        bccOrderRep: false,
        bccClientRep: false,
        requestReadReceipt: false,
        attachPdf: false,
        attachDashboard: false,
      },
    });
    const resendPreviewFromEmail = resendSenderLocked
      ? String(resendSenderPolicy.from?.email || '')
      : String(resendEmailValue.from || '');
    const resendPreviewFromName = resendSenderLocked
      ? String(resendSenderPolicy.from?.name || '')
      : String(financeFromOptions.find((option) => option.id === resendEmailValue.from)?.name || '');
    const [createModeOpen, setCreateModeOpen] = useState(false);
    const [createModeLoading, setCreateModeLoading] = useState(false);
    const [selectedCreateMode, setSelectedCreateMode] = useState(DEFAULT_CREATE_INVOICE_MODE);
    const [enabledCreateInvoiceModes, setEnabledCreateInvoiceModes] = useState(DEFAULT_ENABLED_CREATE_INVOICE_MODES);
    const [configuredDefaultCreateInvoiceMode, setConfiguredDefaultCreateInvoiceMode] = useState(DEFAULT_CREATE_INVOICE_MODE);
    const [createInvoiceAmount, setCreateInvoiceAmount] = useState('');
    const [defaultQboQueueOnCreate, setDefaultQboQueueOnCreate] = useState(false);
    const [queueForQuickBooks, setQueueForQuickBooks] = useState(false);
    const [balanceStatus, setBalanceStatus] = useState(null);
    const [balanceLoading, setBalanceLoading] = useState(Boolean(orderId));
    const [balanceError, setBalanceError] = useState(false);
    const [invoiceListReady, setInvoiceListReady] = useState(!orderId);
    const [resetOpen, setResetOpen] = useState(false);
    const [resetPreview, setResetPreview] = useState(null);
    const [resetLoading, setResetLoading] = useState(false);
    const [resetSubmitting, setResetSubmitting] = useState(false);
    const [resetError, setResetError] = useState('');
    const [preparedInvoiceReview, setPreparedInvoiceReview] = useState(null);
    const [enableInvoiceCreationReview, setEnableInvoiceCreationReview] = useState(false);
    const [addressPrecheck, setAddressPrecheck] = useState(null);
    const [addressPrecheckLoading, setAddressPrecheckLoading] = useState(false);
    const [addressPrecheckError, setAddressPrecheckError] = useState('');
    const [addressPrecheckRefresh, setAddressPrecheckRefresh] = useState(0);
    const [showCompletedAddressDetails, setShowCompletedAddressDetails] = useState(false);
    const [addressReminderSending, setAddressReminderSending] = useState(false);
    const [addressReminderSent, setAddressReminderSent] = useState(false);
    // Keep the Create Invoice dialog in one stable, centered loading state
    // until all initial financial configuration, balance and address checks settle.
    const [createDialogPreparing, setCreateDialogPreparing] = useState(false);
    const [financialConfigReady, setFinancialConfigReady] = useState(!instanceUniqueId);
    const [qboDefaultsReady, setQboDefaultsReady] = useState(!instanceUniqueId);
    const [initialAddressCheckSettled, setInitialAddressCheckSettled] = useState(false);
    const [initialReviewSettled, setInitialReviewSettled] = useState(false);
    const [initialAmountSynced, setInitialAmountSynced] = useState(false);
    const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
    const permissions = useMemo(() => getInvoicePermissionState(authState), [authState]);
    const canManageInvoices = canManageInvoicesProp ?? permissions.canManageInvoices;
    const hasAdministratorRole = normalizeInvoiceRoles(authState).has('administrator');
    // Show the action to administrators; the server validates eligibility on preview and confirmation.
    const canResetInvoiced = hasAdministratorRole;
    const canViewInvoices = canViewInvoicesProp ?? permissions.canViewInvoices;
    const readOnlyInvoiceAccess = readOnlyInvoiceAccessProp ?? (canViewInvoices && !canManageInvoices);
    const safeInvoiceCount = Number.isFinite(Number(invoiceCountProp)) ? Number(invoiceCountProp) : invoices.length;
    const isInvoiceRoute = String(tabtype || '').toLowerCase() === 'invoice';
    const isInvoiceEditRoute = isInvoiceRoute && !!tabId && tabId !== 'new';
    const isInvoiceCreateRoute = isInvoiceRoute && String(tabId || '').toLowerCase() === 'new';

    const visibleCreateInvoiceModeOptions = useMemo(() => {
      const safeEnabledModes = Array.isArray(enabledCreateInvoiceModes) && enabledCreateInvoiceModes.length
        ? enabledCreateInvoiceModes
        : DEFAULT_ENABLED_CREATE_INVOICE_MODES;
      return CREATE_INVOICE_MODE_OPTIONS.filter((option) => safeEnabledModes.includes(option.id));
    }, [enabledCreateInvoiceModes]);

    const resolveDefaultCreateInvoiceMode = () => {
      const safeEnabledModes = visibleCreateInvoiceModeOptions.map((option) => option.id);
      if (safeEnabledModes.includes(configuredDefaultCreateInvoiceMode)) return configuredDefaultCreateInvoiceMode;
      if (safeEnabledModes.includes(DEFAULT_CREATE_INVOICE_MODE)) return DEFAULT_CREATE_INVOICE_MODE;
      return safeEnabledModes[0] || DEFAULT_CREATE_INVOICE_MODE;
    };

    const addressPrecheckSalesOrderId =
      preparedInvoiceReview?.source?.salesOrderId ||
      sourceSalesOrderId ||
      activeInvoicingCartSummary?.sourceSalesOrderDocumentId ||
      (tabtype === 'sales-order' ? tabId : null);

    // Initial opening waits for the optional review to identify the Sales
    // Order. Once opened, changing the invoice amount must not repeatedly
    // restart the address check while a new read-only review is calculated.
    const addressPrecheckCanStart = !createDialogPreparing || (
      financialConfigReady && qboDefaultsReady && invoiceListReady &&
      !balanceLoading && initialAmountSynced &&
      (!enableInvoiceCreationReview || initialReviewSettled)
    );

    useEffect(() => {
      if (!createModeOpen || !orderId || !canManageInvoices ||
          !addressPrecheckCanStart) return undefined;
      let cancelled = false;
      setAddressPrecheck(null);
      setAddressPrecheckError('');
      setAddressPrecheckLoading(true);
      fetchInvoiceAddressPrecheck(orderId, {
        instanceUniqueId,
        salesOrderId: addressPrecheckSalesOrderId,
      }).then((result) => {
        if (!cancelled) setAddressPrecheck(result);
      }).catch((error) => {
        if (!cancelled) {
          setAddressPrecheckError(error?.response?.data?.message || 'Sales Order address details could not be checked.');
        }
      }).finally(() => {
        if (!cancelled) {
          setAddressPrecheckLoading(false);
          setInitialAddressCheckSettled(true);
        }
      });
      return () => { cancelled = true; };
    }, [createModeOpen, orderId, instanceUniqueId, addressPrecheckSalesOrderId, addressPrecheckRefresh, canManageInvoices, addressPrecheckCanStart]);

    const handleNotifyAddressReps = async () => {
      if (!addressPrecheck || addressPrecheck.ready || addressReminderSending || addressReminderSent) return;
      setAddressReminderSending(true);
      try {
        const result = await notifyInvoiceAddressMissing(orderId, {
          instanceUniqueId,
          salesOrderId: addressPrecheck.salesOrderId,
        });
        setAddressReminderSent(true);
        toast.success(`Address reminder sent to ${result?.recipientCount || 0} assigned team member(s).`);
        setAddressPrecheckRefresh((count) => count + 1);
      } catch (error) {
        if (error?.response?.status === 409) {
          setAddressPrecheckRefresh((count) => count + 1);
        }
        toast.error(error?.response?.data?.message || 'Could not send the address reminder.');
      } finally {
        setAddressReminderSending(false);
      }
    };

    const emailContacts = useMemo(() => {
      let contacts = Array.isArray(clientContacts) ? [...clientContacts] : [];
      contacts = contacts.map((contact) => {
        const contactEmail = String(contact?.email || '').trim().toLowerCase();
        const safeName = `${contact?.firstName || ''} ${contact?.lastName || ''}`.trim();
        if (!isValidEmail(contactEmail)) return null;
        return {
          ...contact,
          name: !safeName || safeName === 'undefined undefined' ? contactEmail : safeName,
          id: contactEmail,
        };
      }).filter(Boolean);

      const meEmail = authState?.email || '';
      const normalizedMeEmail = String(meEmail || '').trim().toLowerCase();
      if (isValidEmail(normalizedMeEmail) && !contacts.some((c) => c.id === normalizedMeEmail)) {
        contacts.unshift({ id: normalizedMeEmail, name: `${authState?.name || normalizedMeEmail} (You)` });
      }
      return contacts;
    }, [clientContacts, authState]);

    useEffect(() => {
      const fetchTemplates = async () => {
        if (!instanceUniqueId) return;
        try {
          const headers = { headers: { accessToken: localStorage.getItem('accessToken') } };
          const instanceRes = await axios.get(
            `${process.env.REACT_APP_NODEJS_SERVER_URL}/instances/byinstanceUniqueId/${instanceUniqueId}`,
            headers
          );
          const instanceId = instanceRes.data?.id;
          if (!instanceId) return;
          const templatesRes = await axios.get(
            `${process.env.REACT_APP_NODEJS_SERVER_URL}/instances/${instanceId}/email-templates`,
            headers
          );
          const templates = templatesRes.data?.templates || [];
          setEmailTemplates(templates.filter((template) => template.enabled !== false));
        } catch (error) {
          console.warn('Failed to load email templates', error);
        }
      };

      fetchTemplates();
    }, [instanceUniqueId]);

    useEffect(() => {
      const fetchFinancialConfiguration = async () => {
        setFinancialConfigReady(false);
        if (!instanceUniqueId) {
          setFinancialConfigReady(true);
          return;
        }
        try {
          const headers = { headers: { accessToken: localStorage.getItem('accessToken') } };
          const instanceRes = await axios.get(
            `${process.env.REACT_APP_NODEJS_SERVER_URL}/instances/byinstanceUniqueId/${instanceUniqueId}`,
            headers
          );
          const instanceId = instanceRes.data?.id;
          if (!instanceId) return;
          const defaultsRes = await axios.get(
            `${process.env.REACT_APP_NODEJS_SERVER_URL}/instances/${instanceId}/defaults`,
            headers
          );
          const { enabledModes: safeEnabledModes, defaultMode, enableInvoiceCreationReview: reviewEnabled } = normalizeCreateInvoiceModeConfig(
            defaultsRes.data?.defaults?.financialConfiguration?.createInvoice || {}
          );
          setEnabledCreateInvoiceModes(safeEnabledModes);
          setConfiguredDefaultCreateInvoiceMode(defaultMode);
          setSelectedCreateMode((prev) => safeEnabledModes.includes(prev) ? prev : defaultMode);
          setEnableInvoiceCreationReview(reviewEnabled);
        } catch (error) {
          console.warn('Failed to load financial configuration for invoice creation', error);
          setEnabledCreateInvoiceModes(DEFAULT_ENABLED_CREATE_INVOICE_MODES);
          setConfiguredDefaultCreateInvoiceMode(DEFAULT_CREATE_INVOICE_MODE);
          setEnableInvoiceCreationReview(false);
        } finally {
          // Use existing defaults on failure; don't hold the dialog indefinitely.
          setFinancialConfigReady(true);
        }
      };
      fetchFinancialConfiguration();
    }, [instanceUniqueId]);

    useEffect(() => {
      const fetchQboInvoiceSyncDefaults = async () => {
        setQboDefaultsReady(false);
        if (!instanceUniqueId) {
          setQboDefaultsReady(true);
          return;
        }
        try {
          const headers = { headers: { accessToken: localStorage.getItem('accessToken') } };
          const res = await axios.get(
            `${process.env.REACT_APP_NODEJS_SERVER_URL}/invoices/qbo/settings`,
            { ...headers, params: { instanceUniqueId } }
          );
          const queueOnCreate = res?.data?.qbo_queue_on_create_default === true;
          setDefaultQboQueueOnCreate(queueOnCreate);
          setQueueForQuickBooks(queueOnCreate);
        } catch (error) {
          console.warn('Failed to load QBO invoice sync defaults', error);
          setDefaultQboQueueOnCreate(false);
          setQueueForQuickBooks(false);
        } finally {
          setQboDefaultsReady(true);
        }
      };
      fetchQboInvoiceSyncDefaults();
    }, [instanceUniqueId]);

    useEffect(() => {
      const fetchFinanceFromOptions = async () => {
        if (!instanceUniqueId) return;
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_NODEJS_SERVER_URL}/auth/byinstanceUniqueId/${instanceUniqueId}?role=finance-rep,administrator,super-administrator`,
            { headers: { accessToken: localStorage.getItem('accessToken') } }
          );
          const users = Array.isArray(res?.data?.listOfUsers) ? res.data.listOfUsers : [];
          const options = users
            .map((user) => {
              const email = String(user?.email || '').trim().toLowerCase();
              if (!isValidEmail(email)) return null;
              const name = `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || email;
              return { id: email, email, name };
            })
            .filter(Boolean);
          setFinanceFromOptions(options);
        } catch (error) {
          console.warn('Failed to load finance and administrator users for from-address selection', error);
          setFinanceFromOptions([]);
        }
      };
      fetchFinanceFromOptions();
    }, [instanceUniqueId]);

    const handleOpenResend = async (invoice) => {
      if (!canManageInvoices) {
        toast.error('Invoice changes are restricted to Finance and authorized administrators.');
        return;
      }
      if (!invoice?.id || resendSenderPolicyLoading) return;
      setResendSenderPolicyLoading(true);
      try {
        // Always read the saved instance policy for THIS invoice immediately
        // before showing the composer. No stale employee From value is reused
        // after the administrator switches to a locked sender mode.
        const policyResponse = await axios.get(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/invoices/${invoice.id}/sender-policy`,
          {
            headers: { accessToken: localStorage.getItem('accessToken') },
            params: { instanceUniqueId },
          },
        );
        const policy = policyResponse?.data || {};
        const mode = String(policy.mode || 'user_selectable');
        const locked = mode === 'preferred_user' || mode === 'custom_email';
        if (mode !== 'user_selectable' && !locked) {
          throw new Error('The invoice email sender policy is invalid.');
        }
        const configuredEmail = String(policy.from?.email || '').trim().toLowerCase();
        if (locked && !isValidEmail(configuredEmail)) {
          throw new Error('The configured invoice sender is unavailable. Check Financial Configuration.');
        }
        // Check the actual saved invoice, not only the latest Sales Order.
        // Old invoices may have incomplete address snapshots even when the
        // current project precheck is green.
        let missingAddresses = [];
        try {
          const invoiceResponse = await getPublicInvoice(invoice.id);
          const document = invoiceResponse?.data;
          missingAddresses = document?.id
            ? missingInvoiceAddresses(document)
            : ['Billing and shipping address verification'];
        } catch (_error) {
          missingAddresses = ['Billing and shipping address verification'];
        }
        setResendMissingAddresses(missingAddresses);
        setConfirmSendWithMissingAddresses(false);
        const defaultToId = invoice?.clientContact || invoice?.emailAddress || '';
        const defaultTo = emailContacts.find((contact) =>
          String(contact.id || '').toLowerCase() === String(defaultToId || '').toLowerCase()
        );
        setResendSenderPolicy({ mode, from: locked ? policy.from : null });
        setResendTarget(invoice);
        setResendEmailValue((prev) => {
          const currentOption = financeFromOptions.find((option) =>
            String(option.id || '').toLowerCase() === String(authState?.email || '').toLowerCase()
          );
          const previousOption = financeFromOptions.find((option) =>
            String(option.id || '').toLowerCase() === String(prev.from || '').toLowerCase()
          );
          return {
            ...prev,
            to: defaultTo ? [defaultTo] : [],
            from: locked ? configuredEmail : (currentOption?.id || previousOption?.id || financeFromOptions[0]?.id || ''),
            subject: buildInvoiceEmailSubject({ invoice, orderId, order }),
            body: buildInvoiceEmailBody({ invoice, orderId, order }),
            layout: getPreferredInvoiceLayout(prev.layout),
          };
        });
        setResendOpen(true);
      } catch (error) {
        toast.error(error?.response?.data?.message || error?.message || 'Unable to load invoice sender settings.');
      } finally {
        setResendSenderPolicyLoading(false);
      }
    };

    const toEmails = (list = []) =>
      list
        .map((c) => String(c?.id || '').trim().toLowerCase())
        .filter((email) => isValidEmail(email))
        .filter((email, i, arr) => arr.indexOf(email) === i);

    const handleResend = async () => {
      if (!canManageInvoices) {
        toast.error('Invoice changes are restricted to Finance and authorized administrators.');
        return;
      }
      if (!resendTarget?.id) return;
      if (resendMissingAddresses.length && !confirmSendWithMissingAddresses) {
        toast.error('Review the missing invoice addresses before sending.');
        return;
      }
      const to = toEmails(resendEmailValue.to);
      const ccRaw = toEmails(resendEmailValue.cc);
      const bccRaw = toEmails(resendEmailValue.bcc);
      const cc = ccRaw.filter((email) => !to.includes(email));
      const bcc = bccRaw.filter((email) => !to.includes(email) && !cc.includes(email));
      const from = String(resendEmailValue?.from || '').trim().toLowerCase();

      if (!to.length) {
        toast.error("Choose at least one recipient in 'To'.");
        return;
      }

      setResendSending(true);
      try {
        const resendResult = await resendInvoiceEmail(resendTarget.id, {
          instanceUniqueId,
          email: {
            to,
            cc,
            bcc,
            from: !resendSenderLocked && isValidEmail(from) ? from : undefined,
            subject: resendEmailValue.subject,
            body: resendEmailValue.body,
            templateId: resendEmailValue.templateId || null,
            layout: resendEmailValue.layout || 'plain',
          },
        });
        if (resendResult?.sent === false) {
          toast.info(resendResult.message || 'Email sending is disabled. No invoice email was sent.');
          return;
        }
        toast.success('Invoice email sent.');
        setResendOpen(false);
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to resend invoice email.');
      } finally {
        setResendSending(false);
      }
    };

    const handleClose = () => {
        setInvoiceModalOpen(false);
        history.push(`/instance/${instanceUniqueId}/sales/orders/${orderId}/invoice`)

    };

    const fetchInvoicesList = async () => {
        setInvoiceListReady(false);
        try {
            const response = await fetchInvoicesListByOrder(orderId);
            const list = Array.isArray(response)
              ? response
              : (Array.isArray(response?.data) ? response.data : []);
            setInvoices(list);
            if (typeof onInvoicesChanged === "function") onInvoicesChanged(list);
        } catch (error) {
            console.log(error);
        } finally {
            setInvoiceListReady(true);
        }
    }
    const handleEditClick = (id) => {
        if (!canManageInvoices) {
          if (id) {
            window.open(
              `${process.env.REACT_APP_NODEJS_CLI_URL}/generated-invoice/${id}`,
              '_blank',
              'noopener,noreferrer'
            );
          }
          return;
        }
        const safeId = id || 'new';
        const invoice = (invoices || []).find((row) => String(row?.id) === String(safeId));
        const rel = typeof invoice?.invoiceRelationships === 'string'
          ? (() => { try { return JSON.parse(invoice.invoiceRelationships); } catch (_) { return {}; } })()
          : (invoice?.invoiceRelationships || {});
        const key = invoice?.invoicingCartKey || rel?.invoicingCartKey || null;
        history.push(`/instance/${instanceUniqueId}/sales/orders/${orderId}/invoice/${safeId}${key ? `?invoicingCartKey=${encodeURIComponent(key)}` : ''}`)
        setInvoiceModalOpen(true);
    };

    const handleOpenResetInvoiced = async () => {
      if (!canResetInvoiced) return;
      setResetOpen(true);
      setResetPreview(null);
      setResetError('');
      setResetLoading(true);
      try {
        setResetPreview(await previewResetInvoiced({ orderId, instanceUniqueId }));
      } catch (error) {
        setResetError(error?.response?.data?.message || 'Unable to preview the invoice reset.');
      } finally {
        setResetLoading(false);
      }
    };

    const handleConfirmResetInvoiced = async () => {
      if (!canResetInvoiced || !resetPreview?.canReset || resetSubmitting) return;
      if (!resetPreview.invoiceId) return;
      setResetSubmitting(true);
      setResetError('');
      try {
        const result = await confirmResetInvoiced({
          orderId, instanceUniqueId, invoiceId: resetPreview.invoiceId,

        });
        toast.success(`Invoice #${result.voidedInvoiceId} reset; deposits left untouched.`);
        setResetOpen(false);
        // Both the project status and linked Sales Order status changed. Reload
        // this uncommon admin workflow to refresh the complete project context.
        history.replace(`/instance/${instanceUniqueId}/sales/orders/${orderId}/invoice`);
        window.location.reload();
      } catch (error) {
        setResetError(error?.response?.data?.message || 'Unable to reset invoicing. No changes were applied.');
        setResetPreview(null); // Force fresh precheck after a concurrent change.
      } finally {
        setResetSubmitting(false);
      }
    };

    const handleCreateInvoiceFromMode = async (mode) => {
      if (!canManageInvoices) {
        toast.error('Invoice changes are restricted to Finance and authorized administrators.');
        return;
      }
      const safeMode = visibleCreateInvoiceModeOptions.some((option) => option.id === mode) ? mode : null;
      if (!safeMode) {
        toast.error('This invoice creation option is not enabled.');
        return;
      }
      const remainingInvoiceableLocal = remainingInvoiceable;
      const parsedCreateAmountLocal = Number(createInvoiceAmount);
      const hasCustomInvoiceAmount = createInvoiceAmount !== '' && Number.isFinite(parsedCreateAmountLocal) && parsedCreateAmountLocal > 0;
      const invalidCustomAmountLocal = createInvoiceAmount !== '' && (
        !/^\d+(?:\.\d{1,2})?$/.test(String(createInvoiceAmount).trim()) ||
        !Number.isFinite(parsedCreateAmountLocal) ||
        parsedCreateAmountLocal <= 0
      );

      if (invalidCustomAmountLocal) {
        toast.error('Enter a positive invoice amount with up to two decimal places.');
        return;
      }

      const toCents = (value) => Math.round((Number(value) || 0) * 100);
      const requestedAmount = hasCustomInvoiceAmount
        ? parsedCreateAmountLocal : remainingInvoiceableLocal;
      if (!(Number.isFinite(requestedAmount) && requestedAmount > 0)) {
        toast.error('No invoiceable balance is available. Review existing invoices or the approved Sales Order.');
        return;
      }
      if (toCents(requestedAmount) > toCents(remainingInvoiceableLocal)) {
        toast.error('Requested amount exceeds the uninvoiced balance. Review active invoices or update the approved Sales Order; the amount will not be reduced silently.');
        return;
      }
      const customAmountIsPartial = hasCustomInvoiceAmount && toCents(parsedCreateAmountLocal) < toCents(remainingInvoiceableLocal);
      const invoiceRole = customAmountIsPartial
        ? 'partial'
        : (toCents(invoiceCoverageTotal) > 0 ? 'balance' : 'full');
      try {
        setCreateModeLoading(true);
        const preparationRequest = {
          instanceUniqueId,
          salesOrderId: sourceSalesOrderId || activeInvoicingCartSummary?.sourceSalesOrderDocumentId,
          invoiceRole,
          invoiceMode: safeMode,
          invoiceAmount: hasCustomInvoiceAmount ? parsedCreateAmountLocal : remainingInvoiceableLocal,
          deriveTermsTaxesFrom: 'customer',
        };
        const resolvedReview = await resolveInvoiceCreationPreparation({
          reviewEnabled: enableInvoiceCreationReview,
          preparedReview: preparedInvoiceReview,
          orderId,
          request: preparationRequest,
        });
        // The read-only preparation may cap an amount when payment state
        // changed. Never submit a different amount without Finance approving it.
        const preparedAmountCents = toCents(resolvedReview?.payments?.proposedInvoiceAmount);
        if (preparedAmountCents !== toCents(requestedAmount)) {
          throw new Error('Invoice preparation no longer matches the selected amount. Refresh the balance and try again.');
        }
        const res = typeof onCreateInvoicePathA === 'function'
          ? await onCreateInvoicePathA({
              invoiceMode: safeMode,
              invoiceAmount: resolvedReview?.payments?.proposedInvoiceAmount,
              role: invoiceRole,
              sendEmailNow: false,
              qboQueueOnCreate: queueForQuickBooks,
              preparation: resolvedReview?.preparation,
            })
          : await createInvoice(orderId, {
              instanceUniqueId,
              salesOrderId: tabtype === 'sales-order' ? (tabId || null) : null,
              role: invoiceRole,
              invoiceMode: safeMode,
              sendEmailNow: false,
              qboQueueOnCreate: queueForQuickBooks,
              invoiceAmount: resolvedReview?.payments?.proposedInvoiceAmount,
              deriveTermsTaxesFrom: 'customer',
              invoicingCartKey: resolvedReview?.preparation?.invoicingCartKey,
              preparationRevision: resolvedReview?.preparation?.revisionNumber,
              preparationToken: resolvedReview?.preparation?.preparationToken,
            });
        const createdId = res?.newInvoice?.id || res?.data?.newInvoice?.id || null;
        toast.success(getCreateModeSuccessLabel(safeMode));
        setCreateModeOpen(false);
        await fetchInvoicesList();
        try {
          const next = await fetchOrderBalanceStatus(orderId);
          setBalanceStatus(next || null);
          setBalanceError(false);
        } catch (_) {
          setBalanceError(true);
        }
        if (createdId) {
          handleEditClick(createdId);
        }
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to create invoice.');
      } finally {
        setCreateModeLoading(false);
      }
    };

    const handleConfirmCreateInvoice = async () => {
      if (!canManageInvoices) {
        toast.error('Invoice changes are restricted to Finance and authorized administrators.');
        return;
      }
      const resolvedInvoiceMode = visibleCreateInvoiceModeOptions.some((option) => option.id === selectedCreateMode)
        ? selectedCreateMode
        : visibleCreateInvoiceModeOptions[0]?.id;

      if (!resolvedInvoiceMode) {
        toast.error('No invoice creation option is enabled.');
        return;
      }
      await handleCreateInvoiceFromMode(resolvedInvoiceMode);
    };


    useEffect(() => {
      const access = resolveInvoiceRouteAccess({
        canManageInvoices,
        canViewInvoices,
        invoiceCount: safeInvoiceCount,
        isInvoiceRoute,
        isInvoiceEditRoute,
        isInvoiceCreateRoute,
      });
      if (access === 'allow') return;
      if (access === 'list') {
        history.replace(`/instance/${instanceUniqueId}/sales/orders/${orderId}/invoice`);
        setInvoiceModalOpen(false);
        return;
      }
      history.replace(`/instance/${instanceUniqueId}/sales/orders/${orderId}/overview`);
      setInvoiceModalOpen(false);
    }, [
      canManageInvoices,
      canViewInvoices,
      history,
      instanceUniqueId,
      isInvoiceCreateRoute,
      isInvoiceEditRoute,
      isInvoiceRoute,
      orderId,
      safeInvoiceCount,
    ]);

    useEffect(() => {
      if (String(tabtype || '').toLowerCase() === 'invoice' && tabId && tabId !== 'new') {
        if (!canManageInvoices) return;
        setInvoiceModalOpen(true);
      }
    }, [tabtype, tabId, canManageInvoices]);

    useEffect(() => { 
        if (orderId) fetchInvoicesList(orderId)
    }, [orderId])

    useEffect(() => {
      let cancelled = false;
      const hydrateBalanceStatus = async () => {
        if (!orderId) return;
        setBalanceLoading(true);
        setBalanceError(false);
        try {
          const next = await fetchOrderBalanceStatus(orderId);
          if (!cancelled) {
            setBalanceStatus(next || null);
          }
        } catch (_) {
          if (!cancelled) {
            setBalanceStatus(null);
            setBalanceError(true);
          }
        } finally {
          if (!cancelled) setBalanceLoading(false);
        }
      };
      hydrateBalanceStatus();
      return () => { cancelled = true; };
    }, [orderId, invoices.length]);

    const activeInvoices = useMemo(() => filterActiveInvoices(invoices), [invoices]);

    const editInvoiceTitle = useMemo(() => {
      const invoice = (invoices || []).find((row) => String(row?.id) === String(tabId));
      const invoiceId = invoice?.id || tabId;
      if (!invoiceId || String(invoiceId).toLowerCase() === 'new') return 'Invoice Details';

      const relationships = parseInvoiceRelationships(invoice?.invoiceRelationships);
      const role = String(
        relationships?.role
        || invoice?.invoiceRole
        || invoice?.role
        || ''
      ).trim().toLowerCase();

      return `${role.includes('deposit') ? 'Deposit' : 'Invoice'} #${invoiceId}`;
    }, [invoices, tabId]);

    const paidInvoiceTotal = useMemo(() => (
      activeInvoices.reduce((sum, invoice) => (
        isInvoicePaid(invoice) ? sum + toNumber(invoice?.amount) : sum
      ), 0)
    ), [activeInvoices]);

    const activeInvoiceTotal = useMemo(() => sumActiveInvoiceTotal(activeInvoices), [activeInvoices]);
    const invoiceCoverageTotal = balanceStatus?.activeInvoiceTotal != null
      ? toNumber(balanceStatus.activeInvoiceTotal)
      : activeInvoiceTotal;

    const inferredSalesOrderTotal = useMemo(() => {
      return activeInvoices.reduce((best, invoice) => {
        const rel = parseInvoiceRelationships(invoice?.invoiceRelationships);
        const relTotal = toNumber(rel?.remainingBalance) + toNumber(rel?.depositAppliedAmount);
        return relTotal > 0 ? Math.max(best, relTotal) : best;
      }, 0);
    }, [activeInvoices]);

    const normalizedSalesOrderTotal = (() => {
      const detectedCurrentOrderTotal = toNumber(balanceStatus?.currentOrderTotal);
      if (detectedCurrentOrderTotal > 0) return detectedCurrentOrderTotal;
      const direct = toNumber(salesOrderTotal);
      if (direct > 0) return direct;
      const orderTotal = toNumber(order?.Total);
      if (orderTotal > 0) return orderTotal;
      return inferredSalesOrderTotal;
    })();
    const remainingInvoiceable = resolveRemainingInvoiceableAmount({
      balanceStatus,
      salesOrderTotal: normalizedSalesOrderTotal,
      activeInvoiceTotal: invoiceCoverageTotal,
    });
    const isSalesOrderCovered = normalizedSalesOrderTotal > 0 && paidInvoiceTotal >= normalizedSalesOrderTotal;
    const parsedCreateAmount = Number(createInvoiceAmount);
    const amountExceedsAvailable = createInvoiceAmount !== '' &&
      Number.isFinite(parsedCreateAmount) &&
      Math.round(parsedCreateAmount * 100) > Math.round(remainingInvoiceable * 100);
    const invalidCustomAmount = createInvoiceAmount !== '' && (
      !/^\d+(?:\.\d{1,2})?$/.test(String(createInvoiceAmount).trim()) ||
      !Number.isFinite(parsedCreateAmount) ||
      parsedCreateAmount <= 0 || amountExceedsAvailable
    );
    const invoiceProgressMessage = isSalesOrderCovered
      ? '✅ Complete amount has been paid from sales order.'
      : `🟠 You currently have paid $${paidInvoiceTotal.toFixed(2)} / $${normalizedSalesOrderTotal.toFixed(2)} from sales order.`;
    const shouldShowInvoiceProgress =
      Number(normalizedSalesOrderTotal) > 0 || Number(paidInvoiceTotal) > 0;
    const defaultCreateInvoiceAmount = resolveDefaultCreateInvoiceAmount({
      balanceStatus,
      remainingInvoiceable,
    });
    // While the opening loader is visible, hydrate the initial amount from the
    // finished balance request. Once the form is shown, never override user edits.
    useEffect(() => {
      if (createModeOpen && createDialogPreparing && !balanceLoading && invoiceListReady) {
        setCreateInvoiceAmount(defaultCreateInvoiceAmount);
        setInitialAmountSynced(true);
      }
    }, [createModeOpen, createDialogPreparing, balanceLoading, invoiceListReady, defaultCreateInvoiceAmount]);

    // Only dismiss the initial overlay after every opening dependency settles.
    // Errors also settle their respective checks, so no failed request can
    // leave Finance with an endless spinner.
    useEffect(() => {
      if (!createModeOpen || !createDialogPreparing) return;
      if (!areCreateInvoiceChecksSettled({
        financialConfigReady, qboDefaultsReady, invoiceListReady, balanceLoading,
        initialAmountSynced, initialAddressCheckSettled, addressPrecheckLoading,
        enableInvoiceCreationReview, initialReviewSettled,
      })) return;
      setCreateDialogPreparing(false);
    }, [
      createModeOpen, createDialogPreparing, financialConfigReady, qboDefaultsReady,
      invoiceListReady, balanceLoading, initialAmountSynced, initialAddressCheckSettled,
      addressPrecheckLoading, enableInvoiceCreationReview, initialReviewSettled,
    ]);

    // Parent /invoice page waits for both invoice records and the balance check
    // before revealing the table and its availability message together.
    useEffect(() => {
      if (invoiceListReady && !balanceLoading && typeof onInitialInvoiceDataReady === 'function') {
        onInitialInvoiceDataReady(orderId);
      }
    }, [invoiceListReady, balanceLoading, onInitialInvoiceDataReady, orderId]);

    const additionalBalanceNotice = getInvoiceAvailabilityNotice(balanceStatus);
    const savedOrderMismatchNotice = getSalesOrderInvoiceTotalMismatch({
      balanceStatus,
      liveSalesOrderCartTotal: salesOrderTotal,
    });
    // Existing deposit/balance flow from main: an initial deposit legitimately
    // leaves an uninvoiced final balance. Do not label that normal state an
    // invoice discrepancy or show the generic mismatch banner.
    const depositOnly = hasOnlyDepositInvoices(activeInvoices);
    const showInvoiceNotice = !depositOnly &&
      Boolean(additionalBalanceNotice || savedOrderMismatchNotice);
    // Deposits are legitimate partial coverage, not invoice mismatches.
    const showDepositProgressNotice = depositOnly && remainingInvoiceable > 0;
    const depositPaidTotal = activeInvoices.reduce((sum, invoice) =>
      isInvoicePaid(invoice) ? sum + toNumber(invoice?.amount) : sum, 0);
    const allDepositsPaid = activeInvoices.length > 0 &&
      activeInvoices.every(isInvoicePaid);

    return (
      <>
        {!createModeOpen && showDepositProgressNotice ? (
          <Box data-testid="invoice-deposit-progress-notice" sx={{
            my: 2, px: { xs: 1.75, sm: 2.25 }, py: 1.75,
            bgcolor: '#F5F9FF', border: '1px solid #DCE8FA',
            borderRadius: 2.5,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, width: 30, height: 30, borderRadius: '50%',
                bgcolor: '#E3EDFF', color: '#2B62B6' }}>
                <InfoOutlinedIcon sx={{ fontSize: 19 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontSize: 15, fontWeight: 750, color: '#203653', lineHeight: 1.5 }}>
                  {allDepositsPaid ? 'Deposit paid — remaining balance ready for invoicing'
                    : 'Deposit recorded — payment verification needed'}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.25, fontSize: 12.5, color: '#58708D' }}>
                  {allDepositsPaid
                    ? 'The upfront deposit has been received. The balance is a normal part of this order’s payment terms.'
                    : 'An upfront deposit invoice exists. Verify its payment before collecting the final balance.'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'stretch', flexWrap: 'wrap',
                  mt: 1.35, gap: 1.25 }}>
                  <Box sx={{ minWidth: 155, flex: '1 1 155px', bgcolor: '#FFFFFF',
                    border: '1px solid #E2EAF5', borderRadius: 1.5, px: 1.5, py: 1.05 }}>
                    <Typography variant="caption" sx={{ color: '#677B92', fontSize: 11.5, display: 'block' }}>
                      Deposit received
                    </Typography>
                    <Typography sx={{ fontSize: 20, fontWeight: 750, color: '#137A5A' }}>
                      ${depositPaidTotal.toFixed(2)}
                    </Typography>
                  </Box>
                  <Box sx={{ minWidth: 155, flex: '1 1 155px', bgcolor: '#FFFFFF',
                    border: '1px solid #E2EAF5', borderRadius: 1.5, px: 1.5, py: 1.05 }}>
                    <Typography variant="caption" sx={{ color: '#677B92', fontSize: 11.5, display: 'block' }}>
                      Remaining to invoice
                    </Typography>
                    <Typography sx={{ fontSize: 20, fontWeight: 750, color: '#203653' }}>
                      ${remainingInvoiceable.toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="caption" sx={{ mt: 1.1, display: 'block', fontSize: 12, color: '#52677F' }}>
                  {allDepositsPaid
                    ? 'Next: Create the balance invoice when the remaining payment is due. This is not a billing discrepancy.'
                    : 'Next: Confirm the deposit’s payment status before proceeding. No payment has been assumed.'}
                </Typography>
              </Box>
            </Box>
          </Box>
        ) : null}
        {!createModeOpen && showInvoiceNotice ? (
          <InvoiceDiscrepancyNotice
            balanceStatus={balanceStatus}
            invoices={activeInvoices}
            order={order}
            fallbackNotice={additionalBalanceNotice}
            mismatchNotice={savedOrderMismatchNotice}
            canManageInvoices={canManageInvoices}
            onOpenInvoice={handleEditClick}
          />
        ) : null}
        {/* {shouldShowInvoiceProgress && (
          <Box
            className="status-message"
            sx={{
              backgroundColor: isSalesOrderCovered ? '#d4edda' : '#fff3cd',
              p: 2,
              borderRadius: 2,
              mb: 2,
              border: isSalesOrderCovered ? '1px solid #c3e6cb' : '1px solid #ffeeba',
            }}
          >
            <Typography sx={{ color: isSalesOrderCovered ? '#155724' : '#856404' }}>
              {invoiceProgressMessage}
            </Typography>
          </Box>
        )} */}
        <DepositCheckPanel
          instanceUniqueId={instanceUniqueId}
          orderId={orderId}
          currentUser={authState}
          invoices={invoices}
          variant="finance"
        />
        <InvoiceList
          invoiceCartSelector={invoiceCartSelector}
          invoices={invoices}
          handleEditClick={handleEditClick}
          currentUser={authState}
          canViewInvoices={canViewInvoices}
          canManageInvoices={canManageInvoices}
          readOnlyInvoiceAccess={readOnlyInvoiceAccess}
          onResendInvoice={handleOpenResend}
          resetInvoicedAccess={canResetInvoiced}
          onResetInvoiced={canResetInvoiced ? handleOpenResetInvoiced : undefined}
          onCreateInvoice={() => {
            if (!canManageInvoices) {
              toast.error('Invoice changes are restricted to Finance and authorized administrators.');
              return;
            }
            setSelectedCreateMode(resolveDefaultCreateInvoiceMode());
            setCreateInvoiceAmount(defaultCreateInvoiceAmount);
            setQueueForQuickBooks(defaultQboQueueOnCreate);
            setAddressReminderSent(false);
            setShowCompletedAddressDetails(false);
            setInitialAddressCheckSettled(false);
            setInitialReviewSettled(false);
            setInitialAmountSynced(false);
            setCreateDialogPreparing(true);
            setAddressPrecheckRefresh((count) => count + 1);
            setCreateModeOpen(true);
          }}
           onRefresh={fetchInvoicesList}
  
  showProgress={shouldShowInvoiceProgress}
  paidTotal={paidInvoiceTotal}
  invoiceableTotal={normalizedSalesOrderTotal}
  isCovered={isSalesOrderCovered}
        />
        {readOnlyInvoiceAccess && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Invoice information is available for viewing. Invoice changes are restricted to Finance and authorized administrators.
          </Alert>
        )}
        {canResetInvoiced && (
          <Dialog
            open={resetOpen}
            onClose={() => { if (!resetSubmitting) setResetOpen(false); }}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Reset Invoiced</DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Undo the newest eligible non-deposit invoice and retire its invoicing cart.
                Return the project and Sales Order to Ready to be Invoiced.
                Paid deposits, approved Sales Order amounts, and invoice history stay unchanged.
              </Typography>
              {resetLoading ? (
                <Typography color="text.secondary">Checking the latest invoice and its accounting activity…</Typography>
              ) : null}
              {resetError ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {resetError}

                </Alert>
              ) : null}
              {resetPreview ? (
                <>
                  <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2 }}>
                    {resetPreview.invoice ? (
                      <Typography variant="subtitle2">
                        Invoice #{resetPreview.invoice.id} · {resetPreview.invoice.currency} {resetPreview.invoice.amount.toFixed(2)}
                      </Typography>
                    ) : <Typography variant="subtitle2">No non-deposit invoice found</Typography>}
                    {[
                      { label: 'Project', before: resetPreview.currentProjectStatus },
                      { label: `Sales Order #${resetPreview.salesOrderId || '—'}`, before: resetPreview.currentSalesOrderStatus },
                    ].map(({ label, before }) => {
                      const current = displayInvoiceWorkflowStatus(before);
                      const destination = 'Ready to be Invoiced';
                      return (
                        <Box key={label} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mt: 1.5 }}>
                          <Typography variant="body2" sx={{ minWidth: 110, fontWeight: 600 }}>{label}</Typography>
                          <Chip size="small" variant="outlined" label={current} color={current === 'Invoiced' ? 'success' : 'default'} />
                          <Typography component="span" color="text.secondary" aria-label="changes to">→</Typography>
                          <Chip size="small" color="warning" variant="outlined" label={destination} />
                          {current === destination ? (
                            <Typography variant="caption" color="text.secondary">(unchanged)</Typography>
                          ) : null}
                        </Box>
                      );
                    })}
                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
                      {resetPreview.depositInvoiceIds?.length
                        ? `${resetPreview.depositInvoiceIds.length} deposit invoice(s) remain untouched.`
                        : 'No deposit invoices to preserve.'}
                    </Typography>
                  </Box>
                  {resetPreview.blockers.length ? (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Typography variant="subtitle2">Reset unavailable</Typography>
                      {resetPreview.blockers.map((reason, index) => (
                        <Typography variant="body2" key={index}>• {reason}</Typography>
                      ))}
                    </Alert>
                  ) : (
                    <>
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        This voids only the selected invoice and retires its own invoicing cart.
                        Paid invoices, Stripe checkouts, and exported invoices are blocked.
                        Sent emails cannot be recalled.
                      </Alert>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        This reset is internal only. No emails will be sent and no project notes will be created.
                      </Typography>
                    </>
                  )}
                </>
              ) : null}
            </DialogContent>
            <DialogActions>
              <Button disabled={resetSubmitting} onClick={() => setResetOpen(false)}>Cancel</Button>
              <Button
                color="warning" variant="contained"
                disabled={!resetPreview?.canReset || resetSubmitting}
                onClick={handleConfirmResetInvoiced}
              >
                {resetSubmitting ? 'Resetting…' : 'Confirm Reset Invoiced'}
              </Button>
            </DialogActions>
          </Dialog>
        )}
        {canManageInvoices && (
        <Dialog open={invoiceModalOpen} onClose={handleClose} maxWidth={false} PaperProps={{
            sx: {
                minWidth: '90%',
            },
        }}>
            <DialogTitle className="create-order-dialog-title" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {editInvoiceTitle}
                <IconButton
                    onClick={handleClose}
                    size="large"
                    aria-label="Close invoice"
                    title="Close invoice"
                    sx={{
                        color: '#FFF',
                        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.14)' },
                        '&:focus-visible': { outline: '2px solid #FFF', outlineOffset: 2 },
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
        <DialogContent className="create-order-dialog-content">
        <InvoiceEdit
          fetchInvoicesList={fetchInvoicesList}
          orderId={orderId}
          order={order}
          handleClose={handleClose}
          clientContacts={clientContacts}
          setInvoiceModalOpen={setInvoiceModalOpen}
          onOpenResendInvoice={handleOpenResend}
          invoicingCartKey={invoicingCartKey}
          activeInvoicingCartSummary={activeInvoicingCartSummary}
        />
            </DialogContent>
        </Dialog>
        )}
        {canManageInvoices && (
        <Dialog open={resendOpen} onClose={() => setResendOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle>Resend Invoice via Email</DialogTitle>
            <DialogContent dividers>
                {resendMissingAddresses.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }} data-testid="invoice-resend-missing-addresses">
                    <Typography variant="body2" fontWeight={600}>
                      Invoice address review required
                    </Typography>
                    <Typography variant="body2">
                      {resendMissingAddresses.join(' and ')} could not be confirmed on this saved invoice.
                      Review the invoice and correct the address before sending when possible.
                    </Typography>
                    <FormControlLabel
                      control={<Checkbox
                        checked={confirmSendWithMissingAddresses}
                        onChange={(event) => setConfirmSendWithMissingAddresses(event.target.checked)}
                      />}
                      label="I understand this email may contain an invoice without complete addresses."
                    />
                  </Alert>
                )}
                <EmailComposer
                  contacts={emailContacts}
                  templates={emailTemplates}
                  value={resendEmailValue}
                  onChange={setResendEmailValue}
                  showFromField={true}
                  fromOptions={resendSenderLocked ? [] : financeFromOptions}
                  fromName={resendPreviewFromName}
                  fromEmail={resendPreviewFromEmail}
                  fromHelperText={resendSenderLocked
                    ? 'Fixed by Financial Configuration. From and Reply-To use this address for every invoice email.'
                    : 'Choose a finance or administrator user to send this invoice email.'}
                  attachments={emailAttachments}
                  onAttachmentsChange={setEmailAttachments}
                  quickActionRecipients={{
                    me: authState?.email
                      ? [{ id: authState.email, name: `${authState?.name || authState.email} (You)` }]
                      : [],
                    orderReps: [],
                    clientReps: [],
                  }}
                />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setResendOpen(false)}>Cancel</Button>
              <Button variant="contained" onClick={handleResend} disabled={resendSending || (resendMissingAddresses.length > 0 && !confirmSendWithMissingAddresses)}>
                {resendSending ? 'Sending…' : 'Send Email'}
              </Button>
            </DialogActions>
        </Dialog>
        )}
        {canManageInvoices && (
        <Dialog
          open={createModeOpen}
          onClose={() => {
            if (!createModeLoading) {
              setCreateModeOpen(false);
              setSelectedCreateMode(resolveDefaultCreateInvoiceMode());
              setCreateInvoiceAmount('');
              setQueueForQuickBooks(defaultQboQueueOnCreate);
            }
          }}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: { xs: 2, sm: 3 },
              maxHeight: 'calc(100dvh - 40px)',
              boxShadow: '0 24px 70px rgba(15, 23, 42, 0.18)',
            },
          }}
        >
            <DialogTitle sx={{ px: { xs: 2, sm: 3 }, pt: 2.5, pb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Typography component="span" sx={{ fontSize: { xs: 21, sm: 24 }, fontWeight: 800, letterSpacing: -0.55 }}>
                Create Invoice
              </Typography>
              <IconButton
                size="small"
                aria-label="Close Create Invoice"
                onClick={() => {
                  if (createModeLoading) return;
                  setCreateModeOpen(false);
                  setSelectedCreateMode(resolveDefaultCreateInvoiceMode());
                  setCreateInvoiceAmount('');
                  setQueueForQuickBooks(defaultQboQueueOnCreate);
                }}
                disabled={createModeLoading}
                sx={{ color: 'text.secondary' }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
              {createDialogPreparing ? (
                <Box
                  role="status"
                  aria-live="polite"
                  data-testid="create-invoice-initial-loader"
                  sx={{
                    minHeight: { xs: 310, sm: 390 },
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    justifyContent: 'center', textAlign: 'center', gap: 1.75, px: 2,
                  }}
                >
                  <CircularProgress
                    size={58}
                    thickness={4.1}
                    sx={{ color: '#D71920' }}
                  />
                  <Typography variant="subtitle1" sx={{ mt: 1.25, fontWeight: 800 }}>
                    Preparing invoice details
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360, lineHeight: 1.6 }}>
                    Checking the remaining balance, Sales Order details and project team…
                  </Typography>
                </Box>
              ) : null}
              {/* Keep the review mounted so its read-only preparation can finish
                  behind the loader; hide all calculated content until it settles. */}
              <Box sx={{ display: createDialogPreparing ? 'none' : 'block' }}>
              {showInvoiceNotice ? (
                <InvoiceDiscrepancyNotice
                  balanceStatus={balanceStatus}
                  invoices={activeInvoices}
                  order={order}
                  fallbackNotice={additionalBalanceNotice}
                  mismatchNotice={savedOrderMismatchNotice}
                  compact
                />
              ) : null}
              {/* Informational pre-check only. Invoice creation and email permissions are unchanged. */}
              <Box sx={{ my: 1.5 }} data-testid="invoice-address-precheck">
                {addressPrecheckLoading ? (
                  <Box sx={{ py: 1, color: 'text.secondary' }}>
                    <Typography variant="body2">Checking Sales Order billing and shipping details…</Typography>
                  </Box>
                ) : addressPrecheckError ? (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    <Typography variant="body2">{addressPrecheckError}</Typography>
                    <Button size="small" onClick={() => setAddressPrecheckRefresh((count) => count + 1)}>
                      Retry check
                    </Button>
                  </Alert>
                ) : addressPrecheck?.ready ? (
                  <Box
                    data-testid="invoice-address-precheck-ready"
                    sx={{ px: 1.5, py: 1, borderRadius: 2, border: '1px solid #D5E9DB', bgcolor: '#F7FCF8' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <CheckCircleOutlineIcon sx={{ color: '#17803D', fontSize: 20 }} />
                      <Box sx={{ flex: 1, minWidth: 140 }}>
                        <Typography variant="body2" sx={{ fontWeight: 750 }}>
                          Address details complete
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Billing and shipping contacts and addresses are saved.
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => setShowCompletedAddressDetails((shown) => !shown)}
                        aria-expanded={showCompletedAddressDetails}
                        sx={{ textTransform: 'none', minWidth: 0 }}
                      >
                        {showCompletedAddressDetails ? 'Hide details' : 'View details'}
                      </Button>
                      <Tooltip title="Check for recent address changes">
                        <Button
                          size="small"
                          startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
                          sx={{ textTransform: 'none', minWidth: 0 }}
                          onClick={() => setAddressPrecheckRefresh((count) => count + 1)}
                        >
                          Recheck
                        </Button>
                      </Tooltip>
                    </Box>
                    {showCompletedAddressDetails && (addressPrecheck.fields || []).length > 0 && (
                      <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))' },
                        gap: 0.75,
                        mt: 1.25,
                      }}>
                        {addressPrecheck.fields.map((field) => (
                          <Box
                            key={field.key}
                            sx={{ border: '1px solid #D8E8DD', bgcolor: '#FFFFFF', borderRadius: 2, px: 1.25, py: 1.1, minWidth: 0 }}
                          >
                            <Typography sx={{ fontSize: 12, fontWeight: 700, lineHeight: 1.4 }}>{field.label}</Typography>
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: 'text.secondary', overflowWrap: 'anywhere' }}>
                              {field.preview}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                ) : addressPrecheck ? (
                  <Box
                    data-testid="invoice-address-precheck-missing"
                    sx={{
                      p: { xs: 1.75, sm: 2.25 },
                      borderRadius: 2.5,
                      bgcolor: '#FFFCF8',
                      border: '1px solid #F1BD84',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1.25, sm: 1.75 } }}>
                      <Box sx={{ width: 44, height: 44, flexShrink: 0, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#FFE5C4' }}>
                        <WarningAmberOutlinedIcon sx={{ fontSize: 25, color: '#A84B08' }} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0, pt: 0.1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.3 }}>
                          Sales Order details needed
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary', lineHeight: 1.5 }}>
                          Before emailing this invoice, please complete the following details on the Sales Order.
                        </Typography>
                      </Box>
                      <Box
                        sx={{ px: 1.25, py: 0.65, flexShrink: 0, bgcolor: '#FFE8CF', color: '#9A470A', borderRadius: 3, fontSize: 12, fontWeight: 800 }}
                      >
                        {addressPrecheck.missing?.length || 0} missing
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
                        gap: 1,
                        mt: 2,
                      }}
                    >
                      {(addressPrecheck.missing || []).map((field) => (
                        <Box
                          key={field}
                          sx={{
                            minWidth: 0, display: 'flex', alignItems: 'center', gap: 1,
                            px: 1.25, py: 1.4, bgcolor: '#FFFFFF', borderRadius: 2,
                            border: '1px solid #F1D6B7',
                          }}
                        >
                          {field.toLowerCase().includes('address')
                            ? <PlaceOutlinedIcon sx={{ fontSize: 23, color: '#B46A17', flexShrink: 0 }} />
                            : <PersonOutlineIcon sx={{ fontSize: 23, color: '#B46A17', flexShrink: 0 }} />}
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 750, lineHeight: 1.35 }}>{field}</Typography>
                            <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25 }}>Not added</Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>

                    <Box sx={{ mt: 2.25, pt: 2, borderTop: '1px solid #E4E8EF' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: 15 }}>
                        Project team to notify
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.35, color: 'text.secondary', fontSize: 12.5, lineHeight: 1.5 }}>
                        Only these assigned project members will receive the reminder.
                      </Typography>

                      {addressPrecheck.recipients?.length ? (
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' },
                            gap: 1,
                            mt: 1.4,
                          }}
                        >
                          {addressPrecheck.recipients.map((person) => {
                            const roleLabel = String(person.role || '').replace(/-/g, ' ');
                            const initials = String(person.name || '').trim()
                              .split(/\s+/).slice(0, 2)
                              .map((part) => part.charAt(0).toUpperCase()).join('') || '?';
                            return (
                              <Tooltip key={person.id || person.email} arrow title={`${person.name} · ${roleLabel} · ${person.email}`}>
                                <Box
                                  sx={{
                                    display: 'flex', alignItems: 'center', gap: 1.2,
                                    px: 1.25, py: 1.2, minWidth: 0, bgcolor: '#FFFFFF',
                                    border: '1px solid #DFE5EE', borderRadius: 2,
                                  }}
                                >
                                  <Avatar
                                    src={normalizeAvatarUrl(person.profilePicture) || undefined}
                                    alt={person.name}
                                    sx={{ width: 40, height: 40, bgcolor: '#D8F1E0', color: '#166534', fontWeight: 700, fontSize: 13, flexShrink: 0 }}
                                  >
                                    {initials}
                                  </Avatar>
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{ fontWeight: 750, fontSize: 12.5, lineHeight: 1.35, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {person.name}
                                    </Typography>
                                    <Typography sx={{ fontSize: 11.5, color: 'text.secondary', lineHeight: 1.4, textTransform: 'capitalize' }}>
                                      {roleLabel}
                                    </Typography>
                                    <Typography sx={{ fontSize: 10.5, color: 'text.secondary', lineHeight: 1.35, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {person.email}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Tooltip>
                            );
                          })}
                        </Box>
                      ) : (
                        <Typography variant="caption" sx={{ display: 'block', mt: 1.1, color: 'text.secondary' }}>
                          No eligible order rep, client rep or administrator is assigned to this project.
                        </Typography>
                      )}

                      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.2, mt: 2 }}>
                        {addressPrecheck.recipients?.length ? (
                          <Button
                            size="medium"
                            variant="contained"
                            startIcon={addressReminderSent ? <CheckCircleOutlineIcon /> : <MailOutlineIcon />}
                            onClick={handleNotifyAddressReps}
                            disabled={addressReminderSending || addressReminderSent}
                            sx={{
                              textTransform: 'none', px: 2, minHeight: 40,
                              borderRadius: 2, boxShadow: 'none', fontWeight: 700,
                              bgcolor: '#D71920', '&:hover': { bgcolor: '#B7131C', boxShadow: 'none' },
                            }}
                          >
                            {addressReminderSent ? 'Reminder sent' : addressReminderSending ? 'Sending…' : 'Notify assigned reps'}
                          </Button>
                        ) : null}
                        <Button
                          size="medium"
                          variant="outlined"
                          startIcon={<RefreshIcon sx={{ fontSize: 18 }} />}
                          onClick={() => setAddressPrecheckRefresh((count) => count + 1)}
                          sx={{ textTransform: 'none', minHeight: 40, px: 1.75, borderRadius: 2, color: 'text.primary', borderColor: '#C8D5E4' }}
                        >
                          Recheck details
                        </Button>
                      </Box>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, lineHeight: 1.5 }}>
                      Invoice creation is still available. This is a reminder, not a block.
                    </Typography>
                  </Box>
                ) : null}
              </Box>
              {visibleCreateInvoiceModeOptions.length > 1 ? (
                <>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Choose how this invoice should be created:
                  </Typography>
                  {visibleCreateInvoiceModeOptions.map((option) => (
                    <Box key={option.id} sx={{ mb: 2, p: 1.5, border: '1px solid #eee', borderRadius: 1 }}>
                      <Typography variant="subtitle2">{option.title}</Typography>
                      <Typography variant="caption" display="block">
                        {option.description}
                      </Typography>
                    </Box>
                  ))}
                  <Typography variant="caption" color="text.secondary">
                    Email sending is controlled separately below.
                  </Typography>
                </>
              ) : null}
              <Box sx={{ mt: 2.75 }}>
                <Typography variant="subtitle2" component="label" htmlFor="create-invoice-amount" sx={{ display: 'block', fontWeight: 750, mb: 0.85 }}>
                  Invoice amount
                </Typography>
                <TextField
                  id="create-invoice-amount"
                  fullWidth
                  type="number"
                  size="medium"
                  value={createInvoiceAmount}
                  onChange={(e) => {
                    setCreateInvoiceAmount(e.target.value);
                    setPreparedInvoiceReview(null);
                  }}
                  inputProps={{ min: 0.01, step: '0.01' }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Box sx={{ bgcolor: '#F2F5F8', color: 'text.secondary', borderRadius: 1.2, px: 1, py: 0.6, fontWeight: 700, fontSize: 12 }}>
                          {String(balanceStatus?.currency || order?.currency || 'CAD').toUpperCase()}
                        </Box>
                      </InputAdornment>
                    ),
                    sx: {
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      '& input': { py: 1.4, fontSize: 20, fontWeight: 650, fontVariantNumeric: 'tabular-nums' },
                    },
                  }}
                  error={invalidCustomAmount}
                  helperText={
                    invalidCustomAmount
                      ? (amountExceedsAvailable
                          ? 'This exceeds the available uninvoiced balance. Review existing invoices or revise the Sales Order.'
                          : 'Enter a positive invoice amount with up to two decimal places.')
                      : <InvoiceBalanceHelperText
                          balanceLoading={balanceLoading}
                          balanceError={balanceError}
                          balanceStatus={balanceStatus}
                          defaultCreateInvoiceAmount={defaultCreateInvoiceAmount}
                          remainingInvoiceable={remainingInvoiceable}
                        />
                  }
                />
              </Box>
              {selectedCreateMode && visibleCreateInvoiceModeOptions.length > 1 ? (
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Selected mode: <strong>{getCreateModeLabel(selectedCreateMode)}</strong>
                </Typography>
              ) : null}
              <Box sx={{ mt: 2.5, p: { xs: 1.5, sm: 2 }, borderRadius: 2, border: '1px solid #E6EAF0', bgcolor: '#FAFBFD' }}>
                <FormControlLabel
                  sx={{ m: 0, display: 'flex', alignItems: 'center', '& .MuiFormControlLabel-label': { fontSize: 14, fontWeight: 650 } }}
                  control={(
                    <Switch
                      checked={queueForQuickBooks}
                      onChange={(e) => setQueueForQuickBooks(e.target.checked)}
                    />
                  )}
                  label="Queue for QuickBooks export"
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: { xs: 0, sm: 5.8 }, mt: 0.2, lineHeight: 1.5 }}>
                  Mark this invoice as pending for QuickBooks export after creation. If left off, the invoice can still be exported manually later.
                </Typography>
              </Box>
              {enableInvoiceCreationReview ? <InvoiceCreationReview
                open={createModeOpen && financialConfigReady && qboDefaultsReady && invoiceListReady && !balanceLoading && initialAmountSynced}
                orderId={orderId}
                order={order}
                currency={balanceStatus?.currency || order?.currency || 'CAD'}
                request={{
                  instanceUniqueId,
                  salesOrderId: sourceSalesOrderId || activeInvoicingCartSummary?.sourceSalesOrderDocumentId,
                  invoiceRole: (createInvoiceAmount && Number(createInvoiceAmount) < remainingInvoiceable)
                    ? 'partial'
                    : (invoiceCoverageTotal > 0 ? 'balance' : 'full'),
                  invoiceMode: selectedCreateMode,
                  invoiceAmount: createInvoiceAmount ? Number(createInvoiceAmount) : remainingInvoiceable,
                  deriveTermsTaxesFrom: 'customer',
                }}
                onPrepared={setPreparedInvoiceReview}
                onInitialReviewSettled={setInitialReviewSettled}
              /> : null}
              </Box>
            </DialogContent>
            {!createDialogPreparing ? <DialogActions
              sx={{
                px: { xs: 2, sm: 3 }, py: 2,
                display: 'flex', justifyContent: 'space-between',
                flexWrap: 'wrap', gap: 1.25,
              }}
            >
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => {
                  setCreateModeOpen(false);
                  setSelectedCreateMode(resolveDefaultCreateInvoiceMode());
                  setCreateInvoiceAmount('');
                  setQueueForQuickBooks(defaultQboQueueOnCreate);
                }}
                disabled={createModeLoading}
                sx={{ minHeight: 42, px: 2.5, borderRadius: 2, borderColor: '#CBD3DE', color: 'text.primary', fontWeight: 650, textTransform: 'none' }}
              >
                Cancel
              </Button>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', ml: 'auto' }}>
                {visibleCreateInvoiceModeOptions.length > 1 ? visibleCreateInvoiceModeOptions.map((option) => (
                  <Button
                    key={option.id}
                    variant={selectedCreateMode === option.id ? 'contained' : 'outlined'}
                    onClick={() => setSelectedCreateMode(option.id)}
                    disabled={createModeLoading}
                  >
                    {option.title}
                  </Button>
                )) : null}
                <Button
                  color="success"
                  variant="contained"
                  startIcon={<CheckCircleOutlineIcon />}
                  onClick={handleConfirmCreateInvoice}
                  disabled={createModeLoading || (enableInvoiceCreationReview && !preparedInvoiceReview) || !selectedCreateMode || !createInvoiceAmount || invalidCustomAmount || !visibleCreateInvoiceModeOptions.length}
                  sx={{ minHeight: 42, borderRadius: 2, px: 2.5, fontWeight: 750, textTransform: 'none', boxShadow: 'none' }}
                >
                  {createModeLoading ? 'Creating…' : 'Confirm Create'}
                </Button>
              </Box>
            </DialogActions> : null}
        </Dialog>
        )}
      </>
    );
}
export default Invoices
